import React, { useState, useEffect, useCallback } from 'react';
import { useLoadBingMapScript } from 'hooks/useLoadBingMapScript';
import EVMap from 'components/ChargingLocator/EVMap/EVMap';
import EVLocateMe from 'components/ChargingLocator/EVLocateMe/EVLocateMe';
import EVCutline from 'components/ChargingLocator/EVMap/EVCutline';
import EVFilters from 'components/ChargingLocator/EVFilters/EVFilters';
import ChargingLocatorPanel from 'components/ChargingLocator/ChargingLocatorPanel/ChargingLocatorPanel';
import {
  IChargingStation,
  IEVChargingPageContent,
  IStationFilter,
} from 'interfaces/chargingLocator';
import { useZipcodeData } from 'hooks/useZipcodeData';
import './ChargingLocatorContainer.scss';
import useZipcode from 'hooks/useZipcode';
import ChargingLocatorSkeleton from '../ChargingLocatorSkeleton/ChargingLocatorSkeleton';
import { NoConsentCookieContainer } from 'components/NoConsentCookieContainer/NoConsentCookieContainer';

const useNoCookies = window.UseNoCookiesBingMapsDomain === true;

const baseBingUrl = useNoCookies
  ? 'https://sdk.virtualearth.net'
  : 'https://www.bing.com';

const bingMapScriptUrl = `${baseBingUrl}/api/maps/mapcontrol?key=AouUS5PENTy64iJmE4pv_zud0hnz-buyoVpobPgX0MvVU3qK-ImsDSILPmfCfVlW`;

interface IChargingLocatorContainer {
  stations: IChargingStation[];
  pageContent: IEVChargingPageContent;
  blur: boolean;
}

const ChargingLocatorContainer = ({
  stations,
  pageContent,
  blur,
}: IChargingLocatorContainer): JSX.Element => {
  const [filters, setFilters] = useState<IStationFilter[]>([]);
  const [searchCheck, setSearchCheck] = useState(false);
  const handleSearchCheck = useCallback(() => {
    setSearchCheck(!searchCheck);
  }, [searchCheck]);
  const [panelUpdate, setPanelUpdate] = useState(false);
  const [filteredStations, setFilteredStations] = useState<IChargingStation[]>(
    []
  );
  const [selectedStation, setSelectedStation] = useState<
    IChargingStation | undefined
  >(undefined);

  const bingMapScriptStatus = useLoadBingMapScript(bingMapScriptUrl);
  const { zipcode } = useZipcode();
  const { coordinates } = useZipcodeData(zipcode, 'Error');

  useEffect(() => {
    // initializing zipgate in case the zipcode is not available.
    if (pageContent) {
      const filterList: IStationFilter[] = [];
      (pageContent.filters || []).forEach((filterGroup) => {
        filterGroup.items.forEach((element: IStationFilter) => {
          const filter = structuredClone(element);
          filter.codeList = filter.codes.map((code) => code.code);
          filter.isSelected = false;
          filterList.push(filter);
        });
      });
      setFilters(filterList);
    }
  }, [pageContent]);

  useEffect(() => {
    setPanelUpdate(searchCheck);
  }, [searchCheck]);

  useEffect(() => {
    if (stations) {
      const activeFilters = filters.filter((filter) => filter.isSelected);
      const listCodes: any[] = [];
      const activeCodelists = activeFilters.map(function (filter) {
        listCodes.push(filter.codeList?.[0].toString());
        return listCodes;
      });
      const finalCodes = activeCodelists[0];
      if (activeFilters.length > 0) {
        const matchingStations = stations.filter((station) => {
          var matchFlag = activeFilters.some((filter) => {
            if (filter.codeList?.includes('EVAPP') && station.isEvApp) {
              return true;
            }
            const codeMatch = (station.attributeCodeList || []).filter(
              (element) => (filter.codeList || []).includes(element)
            );
            if (codeMatch.length > 0) {
              return true;
            }
            return false;
          });

          return matchFlag;
        });
        if (finalCodes.indexOf('EVAPP') > -1) {
          var validStations = matchingStations.filter((station) => {
            if (station.isEvApp) {
              return true;
            }
            return false;
          });
          if (activeFilters.length > 1) {
            validStations = validStations.filter((station) => {
              var flag = activeFilters.some((filter) => {
                const codeCheck = (station.attributeCodeList || []).filter(
                  (element) => (filter.codeList || []).includes(element)
                );
                if (codeCheck.length > 0) {
                  return true;
                }
                return false;
              });
              return flag;
            });
            setFilteredStations(validStations);
          }
          setFilteredStations(validStations);
        } else {
          setFilteredStations(matchingStations);
        }
      } else {
        setFilteredStations(stations);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, stations]);

  const handleFilterChange = (selectedFilter: IStationFilter) => {
    const filtersCodes = selectedFilter.codes;
    const updatedFilters = [...(filters as IStationFilter[])];
    updatedFilters.forEach((filter) => {
      if (filter.codes === filtersCodes) {
        filter.isSelected = !filter.isSelected;
      }
    });
    setFilters([...updatedFilters]);
  };

  const renderMapContent = () => {
    switch (bingMapScriptStatus) {
      case 'noConsentCookie':
        return <NoConsentCookieContainer />;
      case 'ready':
        return (
          <EVMap
            labels={{}}
            stations={filteredStations}
            isEmbedded={false}
            coordinates={coordinates}
            isCustomPins={true}
            handleStationChange={setSelectedStation}
            selectedStation={selectedStation}
            blur={blur}
            searchCheck={searchCheck}
            handleSearchCheck={handleSearchCheck}
          />
        );
      default:
        return <ChargingLocatorSkeleton />;
    }
  };

  return (
    <div className='charging-locator-container'>
      {renderMapContent()}
      {bingMapScriptStatus !== 'noConsentCookie' && (
        <>
          <ChargingLocatorPanel
            stations={filteredStations}
            selectedStation={selectedStation}
            handleStationChange={setSelectedStation}
            pageContent={pageContent}
            disable={blur}
            panelUpdate={panelUpdate}
          />
          <EVFilters filters={filters} onFilterChange={handleFilterChange} />
          <EVLocateMe disable={blur} />
          <div className='charging-locator-container__legend'>
            <EVCutline cutlineText={pageContent.labels.cutline} />
          </div>
        </>
      )}
    </div>
  );
};

export default ChargingLocatorContainer;
